<template>
  <div class="entities col-xl-5 mx-auto mt-4">
    <h5 v-html="beforeEntitiesMessage"></h5>
    <p v-if="!beforeEntities.length">You don't have any entities. Click the button below to add one.</p>
    <div v-else>
      <b-table striped :items="beforeEntities" :fields="fields" class="table-light">
        <template v-slot:cell(entityType)="data">
          {{ entityTypeDescription(data.item.entityTypeID) }}
        </template>
        <template v-slot:cell(actions)="data">
          <b-button
            :to="{ name: 'entity/edit', params: { submissionId: submission.submissionID, changeId: change.changeID, entityId: data.item.entityID }}"
            size="sm"
            variant="success">{{ isViewOnly ? 'View' : 'Edit' }}</b-button>
          <b-button 
            @click.prevent.stop="deleteClicked(data.item.entityID)" 
            variant="danger" 
            size="sm" 
            class="ml-1"
            v-if="!isViewOnly">Delete</b-button>
        </template>
      </b-table>
    </div>
    <b-button
      :to="{ name: 'entity/edit', params: { submissionId: submission.submissionID, changeId: this.change.changeID, before: true } }"
      variant="success"
      v-if="!isViewOnly">{{ beforeButtonText }}</b-button>
    <div v-if="changeTypeAllowsAfterEntities">
      <h5 class="mt-5">Entities AFTER this <b>{{ changeTypeDescription(this.change.changeTypeID) }}</b></h5>
      <p v-if="!afterEntities.length">You don't have any entities. Click the button below to add one.</p>
      <div v-else>
        <b-table striped :items="afterEntities" :fields="fields" class="table-light">
          <template v-slot:cell(entityType)="data">
            {{ entityTypeDescription(data.item.entityTypeID) }}
          </template>
          <template v-slot:cell(actions)="data">
            <b-button
              :to="{ name: 'entity/edit', params: { submissionId: submission.submissionID, changeId: change.changeID, entityId: data.item.entityID }}"
              size="sm"
              variant="success">{{ isViewOnly ? 'View' : 'Edit' }}</b-button>
            <b-button 
              @click.prevent.stop="deleteClicked(data.item.entityID)" 
              variant="danger" 
              size="sm" 
              class="ml-1"
              v-if="!isViewOnly">Delete</b-button>
          </template>
        </b-table>
      </div>
      <b-button
        :to="{ name: 'entity/edit', params: { submissionId: submission.submissionID, changeId: this.change.changeID, before: false } }"
        variant="success"
        v-if="!isViewOnly">{{ 'Add' + (this.afterEntities.length ? ' Additional' : '') + ' Entity After Change'}}</b-button>
    </div>
    <b-alert show variant="danger" v-if="entityErrors.length" class="mt-5 text-left">
      <h4 class="mb-0">Error{{ entityErrors.length == 1 ? '' : 's' }}</h4>
      <hr class="mt-1">
      <ul>
        <li v-for="(ee, i) in entityErrors" :key="i">
          {{ ee }}
        </li>
      </ul>
    </b-alert>

    <b-modal ref="delete-modal" title="Are you sure?" header-text-variant="dark" body-text-variant="dark" centered>
      <template slot="default">
        Are you sure you want to delete this entity?
      </template>
      <template slot="modal-footer" class="float-right">
        <b-button variant="danger" @click.prevent="deleteEntity" :disabled="isDeleting">
          <span v-if="!isDeleting">Delete</span>
          <span v-else><b-spinner small></b-spinner> Please Wait...</span>
        </b-button>
        <b-button variant="secondary" @click.prevent="cancelClicked">Cancel</b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import EntityService from '../services/EntityService.js'

export default {
  name: 'Entities',
  data() {
    return {
      isDeleting: false,
      entityErrors: [],
      fields: [
        { key: 'entityName' , label: 'Name', sortable: true, thStyle: { width: '55%' } },
        { key: 'entityType' , label: 'Type', sortable: true, thStyle: { width: '30%' } },
        { key: 'actions', label: '', sortable: false, class: ['action-buttons'] }
      ],
      entityIdToDelete: ''
    }
  },
  methods: {
    changeTypeDescription(id) {
      const changeType = this.changeTypes.find(c => c.changeTypeID == id)
      return changeType ? changeType.shortDescription : ''
    },
    entityTypeDescription(id) {
      return this.entityTypes.find(e => e.entityTypeID == id).description
    },
    deleteClicked(id) {
      this.$refs['delete-modal'].show()
      this.entityIdToDelete = id
    },
    cancelClicked() {
      this.$refs['delete-modal'].hide()
      this.entityIdToDelete = ''
    },
    deleteEntity() {
      this.isDeleting = true
      this.$store.dispatch('entity/delete', this.entityIdToDelete).then(() => {
        this.isDeleting = false
        this.$refs['delete-modal'].hide()
        this.entityIdToDelete = ''
      }).catch(() => {
        this.isDeleting = false
      })
    },
    backClicked() {
      this.$router.push({
        name: 'change/edit',
        params: {
          submissionId: this.change.submissionID,
          changeId: this.change.changeID
        }
      })
    },
    nextClicked() {
      if (this.isViewOnly) {
        this.$router.push({
          name: 'questions',
          params: {
            submissionId: this.change.submissionID,
            changeId: this.change.changeID
          }
        })
      } else {
        EntityService.validate(this.change.changeID).then(() => {
          this.$router.push({
            name: 'questions',
            params: {
              submissionId: this.change.submissionID,
              changeId: this.change.changeID
            }
          })
        }).catch(res => {
          this.entityErrors = res.response.data
        })
      }
    }
  },
  computed: {
    beforeEntities() {
      return this.entities.filter(e => e.beforeChange).sort((a, b) => a.entityID - b.entityID)
    },
    afterEntities() {
      return this.entities.filter(e => !e.beforeChange).sort((a, b) => a.entityID - b.entityID)
    },
    changeTypeAllowsAfterEntities() {
      const changeType = this.changeTypes.find(c => c.changeTypeID == this.change.changeTypeID)
      return changeType ? changeType.allowsAfterEntities : true
    },
    beforeEntitiesMessage() {
      return `Entities ${ this.changeTypeAllowsAfterEntities ? 'BEFORE' : 'in' } this <b>${ this.changeTypeDescription(this.change.changeTypeID) }</b>`
    },
    beforeButtonText() {
      return `Add ${ this.beforeEntities.length ? 'Additional ' : ''}Entity ${ this.changeTypeAllowsAfterEntities ? 'Before Change' : ''}`
    },
    ...mapGetters({
      entities: 'entity/entitiesForChange',
      isViewOnly: 'application/isChangeViewOnly'
    }),
    ...mapState({
      submission: state => state.submission.submission,
      change: state => state.change.change,
      changeTypes: state => state.application.changeTypes,
      entityTypes: state => state.application.entityTypes
    })
  },
  created() {
    this.$EventBus.$on('back-clicked', this.backClicked)
    this.$EventBus.$on('next-clicked', this.nextClicked)
  },
  beforeDestroy() {
    this.$EventBus.$off('back-clicked', this.backClicked)
    this.$EventBus.$off('next-clicked', this.nextClicked)
  }
}
</script>